import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { cn } from '../lib/classnames.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { bgRegistrationScale } from '../spatial/registration.js';

/**
 * LivingSculpt — a gilding presented inside a carved gold 3D frame.
 *
 * spatial2 rewrite: render state decided by ONE fetch of the gilding's
 * `spatial2/<genId>-<coverIdx>/meta.json` manifest (immutable, versioned):
 *
 *  - SPATIAL (`meta.mode === "ldi" && meta.v === 2`): the server-validated
 *    2-layer LDI — a premultiplied subject cutout (fg.png) over a clean
 *    inpainted background plate (bg.png), registered EXACTLY through the rest
 *    camera (bg scaled by the world-space camera-distance ratio) and animated
 *    by CAMERA ORBIT ONLY. No per-plane translation amps exist, so the
 *    "differential amp slide" bug class cannot recur.
 *
 *  - SOLO (`meta.mode === "solo" && meta.v === 2`, the 2026-07-03 NO-FLAT
 *    decision): ONE continuous full-frame mesh — the cover verbatim
 *    (solo.png) displaced by the whole-cover depth map (solodepth.png) at the
 *    opening plane. NO cutout, NO bg plate, NO fill → zero disocclusion, so a
 *    fill defect is impossible by construction: every pixel is real art.
 *    Subtler pop than the 2-layer lift, but always clean. Same camera-orbit
 *    motion, same pose-gained displacement (rest frame == cover exactly).
 *
 *  - FLAT (anything else — meta missing/version mismatch, texture load
 *    error, no spatialUrl): the original cover as a premium framed print with
 *    the whole-card tilt. The ultimate degrade only (reduced-motion / no
 *    WebGL / broken assets) — the pipeline no longer emits flat bundles.
 *
 * The legacy ghosting paths (original-art bg plate, N-band MPI, client
 * PatchMatch inpaint, LDI_GENS, safety backing, MirroredRepeat content wrap,
 * depth-URL string-replace + HEAD probes) are deleted, not disabled.
 *
 * Robust by design: reduced-motion or no-WebGL falls back to the plain image.
 */

/** ONE ornate baroque gold frame, rendered as a 9-SLICE so it wraps ANY aspect:
 *  the corner cartouches stay crisp (fixed-size corner quads) and only the edge
 *  strips stretch along their length. The opening is the image rectangle exactly,
 *  so the whole gilding fits — no crop, no gap, no distortion, at every aspect. */
const FRAME_SRC = 'https://api.gilding.app/images/assets/frame-ornate.png';
const FRAME_T = 0.27; // uv inset of the molding (corner/edge slice size) in the source
const FRAME_MT = 0.42; // molding world thickness
const FRAME_OPEN_H = 2.06; // opening world height (image height); width = H * aspect
// The gold fills the OUTER (1 - FRAME_TRANS) of each slice (the inner part is the
// source opening). Place pieces so the GOLD inner edge — not the slice edge —
// meets the image, overlapping by FRAME_OVL (world) so there's NO dark gap ring.
const FRAME_TRANS = 0.19;
const FRAME_OVL = 0.05;
// The 9-slice frame's GOLD outer edge actually reaches only `oh/2 + FRAME_OUTER`
// (pieces are placed by their gold inner edge), NOT `oh/2 + FRAME_MT`. Using the
// TRUE extent for the canvas + fill-scale keeps the frame meeting the canvas edge
// exactly (no near-black margin "void").
const FRAME_OUTER = FRAME_MT * (1 - FRAME_TRANS) - FRAME_OVL;
/** Frame OUTER aspect (the canvas shape) for a given gilding aspect. */
export function frameOuterAspect(imgAspect: number): number {
  const oh = FRAME_OPEN_H;
  const ow = oh * imgAspect;
  return (ow + 2 * FRAME_OUTER) / (oh + 2 * FRAME_OUTER);
}

/* ── spatial2 scene constants (spec §4 / §8) ─────────────────────────────── */
const CAM = 6.2; // camera rest distance (world), fov 28, lookAt origin
// fg → bg layer separation. The subject↔bg disocclusion (the "see-behind"
// reveal, and the dark matte-edge SKIRT that rides its outer boundary) scales as
// orbit × (SEP − FG_Z). Widening the orbit 0.19→0.25 (2026-07-04) over-exposed
// that skirt into a visible line tracing the lifted subject. 0.55→0.45 restores
// the disocclusion to EXACTLY the previously-loved 0.19-orbit amount
// (0.25·(0.45−0.11) ≈ 0.19·(0.55−0.11)) while KEEPING the wider rotation — the
// see-behind stays clearly visible, the seam is no longer over-exposed.
const SEP = 0.45;
// Calmed 0.30/0.18 -> 0.19/0.11 after user review (2026-07-02): the full-amp
// orbit read as "too dramatic/glitchy" vs the old renderer's feel. The mat lip
// stays sized for the old 0.30 excursion = extra safety margin.
// 0.19/0.11 -> 0.25/0.15 (user 2026-07-04: "a little more rotated, not dizzy or
// exaggerated"): +32% swing RANGE for more visible parallax/angle, while the
// 6.2s period is unchanged so the SPEED stays calm (dizziness comes from speed,
// not range). Still under the mat-lip occlusion budget (verified void-clean).
const ORBIT_AMP_X = 0.32; // camera-orbit amplitude (world) per unit drive. 0.25->0.32
const ORBIT_AMP_Y = 0.2; // 0.15->0.20. User 2026-07-05: the live orbit + download moved too
// subtly — the depth didn't "pop" like the static preview sheets. More camera travel = more
// parallax = more apparent depth, WITHOUT the light-beam bend (that was the depth-map scene-tilt,
// a different knob). Shared by the live ambient orbit AND the export captureClip, so preview and
// download stay identical. Edges hold at this range via the 4-edge vplant + set-back mat occluder.
// 0.18 → 0.45 → 0.9-BIPOLAR (user reviews 2026-07-02): the displacement is now
// signed about the plate — (depth−0.5)·D_BG — so the near ground rises ~0.45
// to meet the subject (kills the floating-cutout tell) AND the far field
// recedes ~0.45 beyond the plate (the scenery itself reads deep).
const D_BG = 1.25; // Phase-2 bgdepth displacement RANGE (world, bipolar; 0.9->1.25 deeper scene)
// SOLO continuous-mesh displacement RANGE (world, bipolar about the opening
// plane). It is the ONLY depth cue in solo mode so it runs a touch stronger
// than the ldi bg plate (0.9), but through the SAME border ramp + soft-clamp
// (SP_BG_VERT) so edges stay pinned (no tears) and depth outliers can't swim.
const D_SOLO = 1.28; // 1.28 = the "absolutely amazing" baseline the user signed off on. 2026-07-05
// DEPTH-BOOST ATTEMPT FAILED and was reverted: on the single-surface displacement path a straight
// FLAT feature (light shaft) painted across the scene's depth is SHEARED whenever the surface is
// displaced + viewed off-axis. Isolation tests proved (a) tilt is the biggest bend driver but
// (b) even tilt=0 with raw-depth amplitude still bends it (user: "literally all of those are bent").
// So there is NO displacement setting that gives more depth AND a straight beam — the only truly
// straight beam is zero displacement (flat). Boosting depth ⇒ more beam bend, period. The REAL fix
// for straight-beam-WITH-depth is a representation change (give atmospheric shafts/sky their own
// flat depth so they translate rigidly instead of shearing) — a depth-map reprocess, not a constant.
// Reverted to the loved 1.28 pending that decision.
// SOLO center-peak softening (user 2026-07-04: "the middle is moved more than the edges").
// The near subject sits centred, so a tall depth peak there parallaxes far more than the
// pinned edges → the middle "swims". A tighter knee + stronger compression flattens ONLY the
// extreme near/far peaks (keeps mid-range scene depth) so centre vs edge motion is more even.
// The LDI bg plate keeps its original 0.35/0.25 (its motion is already right). Live-tunable via
// window.__soloDisp / __soloKnee / __soloKneeComp for the sweep.
const SOLO_KNEE = 0.30;
const SOLO_KNEE_COMP = 0.18;
// SOLO ground-plane prior. Monocular depth collapses backgrounds to a flat far plane (verified:
// the lava scene's mountains/sky are all black in solodepth), so ALL parallax lands on the
// centred subject → it swims while the scene sits still. Adding a vertical depth tilt gives the
// whole frame receding-ground parallax (rich, EVEN 3D) without changing the rest image (ray comp).
const SOLO_SCENE_TILT = 0.18; // 2026-07-06: cut 0.32→0.18 to reduce the subject-SILHOUETTE wave (user
// saw the whale's outline buckle in motion). Tilt is the biggest edge-WARP driver, so trimming it gentles
// the outline while D_SOLO stays 1.28 (the loved pop is preserved). Verified geometric, not aliasing (depth
// silhouette is a clean arc; mesh/half-float/512-disp all left it unchanged). Loved baseline was 0.32.
// beam-bend driver (a vertical shaft shears through this artificial vertical gradient), but even tilt=0
// still bends via raw-depth displacement, so cutting it isn't a real fix. Even-depth prior; the edge taper (in the overscan margin) keeps
// the tilt excursion off the visible edges. Content-adaptive scale flattens it on graphic posters.
// SOLO OVERSCAN (FIX 1, user 2026-07-04 "edges not moving proportionately + edge
// distortion on letters/lines"): the border taper used to ramp displacement
// 0→full across [9%,24%] of each axis — INSIDE the visible opening — so content
// there (straight lines/letters near the edge) rode a displacement GRADIENT and
// warped, and the pinned outer band lagged the interior. The fix: grow the solo
// mesh OUTWARD by SOLO_OVERSCAN world per side so the whole taper ramp falls in
// the region HIDDEN UNDER THE MAT RING; the visible opening then shows only the
// FULL, untapered displacement (edges move exactly like the interior). Kept
// strictly < MAT_OUT (0.15) so the mesh's outer edge stays occluded by the mat
// at every orbit pose (ray-comp keeps the rest frame == cover regardless).
// SOLO SET-BACK (2026-07-05): the reverted coplanar overscan failed because the
// solo mesh sat AT the opening plane (z=0) — oversizing it there poked past the
// gold's ornate hollows AND near-field-bottom scenes tore under vertical orbit
// (a coplanar mesh can't be occluded by the frame). The fix reproduces the LDI
// bg plate exactly: the solo mesh is SET BACK behind the opening by SEP_SOLO and
// REGISTRATION-SCALED (bgRegistrationScale) so it still projects pixel-onto the
// opening at rest, but its oversized boundary now lands BEHIND the opaque mat
// ring + gold frame at every pose (the bg plate is void-clean this way, and its
// near-ground lift never tears because the boundary is occluded, not coplanar).
// With the boundary hidden, the VISIBLE opening carries FULL, untapered
// displacement → edges move proportionately with the interior.
const SEP_SOLO = SEP; // same set-back as the ldi bg plate (proven mat-lip occlusion budget).
// Overscan grows the mesh past the opening by SOLO_OVERSCAN (world) per side so
// the border TAPER lives entirely in that now-hidden margin band; the cover fills
// the inner opening region at tap=1. Kept < MAT_OUT (0.15) so the mesh's outer
// edge stays under the mat at every orbit pose (registration keeps rest == cover).
const SOLO_OVERSCAN = 0.1;
// CONTENT-ADAPTIVE relief (FIX 2): a flat GRAPHIC poster (bold letters + hard
// vector/ink edges) must get MINIMAL relief + tilt so its straight lines don't
// warp; a photoreal SCENE keeps full depth. graphicReliefScale() scores the
// cover by its strong-edge fraction (bold ink/text survives an aggressive
// de-halftone downsample; photo gradients don't) and maps it to [FLOOR,1]. Both
// D_SOLO and SOLO_SCENE_TILT are multiplied by the score.
const RELIEF_EDGE_LO = 0.03; // strong-edge fraction at/below → full relief (scene)
const RELIEF_EDGE_HI = 0.217; // strong-edge fraction at/above → floor relief (heavy comic)
const RELIEF_SCALE_FLOOR = 0.70; // 0.15->0.70. User 2026-07-05: posters lost their 3D. The set-back
// mesh fix removed the edge taper that was the main cause of poster TEXT warp, so graphics can now
// take strong depth without distorting (verified: comic text stays straight at uDisp up to 1.1).
// Keep a mild reduction only as a safety margin on the very heaviest ink.
const RELIEF_REDUCTION = 0.30; // 1 - FLOOR
// Border-taper band for SP_BG_VERT (solo + ldi bg plate). Displacement is a RIGID
// ZERO in the outer `TAP_EDGE0` of each axis (no gradient ⇒ straight lines stay
// straight where they meet the frame) then C1-ramps to full over [EDGE0,EDGE1].
// Old 0.03/0.12 left the ramp inside the visible area and steep → straight
// structures bent near the edges at the 0.25 orbit (user 2026-07-04).
const TAP_EDGE0 = 0.09;
const TAP_EDGE1 = 0.24;
// Device review 2026-07-02 ("whale feels separate from the image"): the
// subject no longer sits ON the window plane — it lives INSIDE the scene at
// z=-FG_Z (rest-identical via registration scaling), so motion shows three
// depth planes: static frame → gently drifting subject → deeper environment.
const FG_Z = 0.11; // 0.22->0.11: plant the subject near the scene so it moves WITH it (no float)
// Posters push the mascot much further forward (0.05 world = strong pop out of
// the logo). Big fg/bg separation is safe on flat graphic art: the uniform
// paper behind fills cleanly.
const POSTER_FG_Z = 0.05;
// Fraction of the fg→bg gap the silhouette rim recedes. 1.0 = the rim sits
// EXACTLY on the bg plane, so its screen motion matches the scene at ANY orbit
// amplitude (0.85 left a 15% residual parallax that the wider swing re-exposed
// as a seam — user 2026-07-04). The body keeps its full see-behind lift; only
// the low-alpha rim is pinned to the scene.
const EDGE_RECEDE_FRAC = 1.0;
const D_FG = 0.1; // poster micro-relief amplitude (world)
const D_FG_SCENE = 0.30; // pop now comes from the subject's OWN depth volume, not plane separation
const FG_RELIEF_MIN_STRUCTURE = 0.28; // meta.depthStructure gate for fg micro-relief
const POSTER_BG_TEXTURE_MAX = 0.1; // meta.bgTexture below this = poster treatment
const POSTER_FG_RELIEF_BOOST = 7.0; // flat-art posters need a strong lone cue to dome the mascot fwd
// Flat-print tilt per unit drive — the whole framed card (content + mat + gold)
// rotates as one rigid object, revealing nothing. ~9° yaw at full deflection.
const TILT_AMP_X = 0.21; // flat/solo whole-card tilt, scaled up with the orbit
const TILT_AMP_Y = 0.13;
// Light choreography per unit drive: the key light tracks the pointer (the gold's
// "real material" tell) and the same swing drives the clamped content relight.
const KEY_SWING = 0.95;
const LIGHT_SWING = 0.5;
const RELIGHT_NORMAL_GAIN = 2.2; // depth-gradient → normal steepness for relight

/* ── spatial2 shaders ──────────────────────────────────────────────────────
   Content renders through raw ShaderMaterials: NoColorSpace textures sampled and
   written back untouched (no tone mapping, no colour transform), so the rest
   frame is byte-comparable to the source cover. The gold frame keeps its ACES +
   key-light PBR look via its own MeshStandardMaterial. */

/* Neutral-at-rest clamped relight. s = n·L − n.z·L.z = nx·Lx + ny·Ly, which is
   EXACTLY 0 at the rest light (0,0,1) for every pixel AND 0 for flat normals
   under any light — so the render is pixel-identical to the source at rest (the
   CI rest-identity gate) and flat regions never dim in motion. Clamps bound the
   multiplier so steep depth gradients can never crush to black or blow out. */
const SP_RELIGHT = /* glsl */ `
  float relightMul(sampler2D depth, vec2 uv, vec2 texel, float relief, vec3 light, float lo, float hi){
    float l = texture2D(depth, clamp(uv - vec2(texel.x, 0.0), vec2(0.001), vec2(0.999))).r;
    float r = texture2D(depth, clamp(uv + vec2(texel.x, 0.0), vec2(0.001), vec2(0.999))).r;
    float d = texture2D(depth, clamp(uv - vec2(0.0, texel.y), vec2(0.001), vec2(0.999))).r;
    float u = texture2D(depth, clamp(uv + vec2(0.0, texel.y), vec2(0.001), vec2(0.999))).r;
    vec3 n = normalize(vec3((l - r) * relief, (d - u) * relief, 1.0));
    vec3 L = normalize(light);
    float s = dot(n, L) - n.z * L.z;
    return clamp(1.0 + 1.35 * s, lo, hi);
  }
`;

/* BG plate vertex. Phase 1 ships with uDisp = 0 (flat clean plate). Phase 2
   (flag `spatial2Relief`) displaces along the REST-camera view ray: z toward the
   camera, xy shrunk by the same perspective ratio — pixel-identical to the flat
   plate at rest; intra-bg parallax appears only off-axis. Displacement (NEVER
   alpha) is ZERO within 3% of the plate edge and ramps in over the next 9%
   (defect hunt D1: the old 8% edge-terminated taper left the full bipolar
   gradient right at the border, where the xy ray compensation folded rows over
   the pinned edge into stepped black tears under orbit — a rigid margin +
   gentler ramp keeps the border monotonic at every pose). */
const SP_BG_VERT = /* glsl */ `
  precision highp float;
  uniform sampler2D uDispMap; // displacement depth (r: 1 = near; see bgDispTex)
  uniform float uDisp;        // world-unit displacement amplitude (0 = Phase 1)
  uniform float uCamDist;     // world distance camera → this plane at rest
  uniform float uZScale;      // world units per geometry-local z unit
  uniform float uEdge0;       // border taper: ramp START (rigid-zero band = [0,uEdge0])
  uniform float uEdge1;       // border taper: ramp END (full displacement from uEdge1 in)
  uniform float uKnee;        // soft-clamp knee on |depth-0.5| (linear below, compressed above)
  uniform float uKneeComp;    // compression factor beyond the knee (lower = flatter center peak)
  uniform float uSceneTilt;   // SOLO ground-plane prior: adds vertical depth so a flat-background
                              // scene recedes (top far, bottom near) → parallax across the whole
                              // frame, not just the centred subject. 0 for the ldi bg plate.
  uniform float uOvsX;        // SOLO OVERSCAN: cover-region inset in mesh-uv per side (0 = none/ldi).
  uniform float uOvsY;        // The cover fills mesh-uv [uOvs, 1-uOvs]; the outer band is hidden
                              // under the mat so the border TAPER lives there and the VISIBLE
                              // opening shows only full, untapered displacement (edges move
                              // proportionately; no gradient warps straight lines at the frame).
  varying vec2 vUv;
  void main(){
    // Mesh-uv (uv) drives the taper; COVER-uv (cuv) samples the art + depth. They
    // are identical (cuv==uv) when uOvs==0 → the LDI bg plate is byte-unchanged.
    vec2 cuv = uv;
    if (uOvsX > 0.0) cuv.x = clamp((uv.x - uOvsX) / (1.0 - 2.0 * uOvsX), 0.0, 1.0);
    if (uOvsY > 0.0) cuv.y = clamp((uv.y - uOvsY) / (1.0 - 2.0 * uOvsY), 0.0, 1.0);
    vUv = cuv;
    vec3 p = position;
    if (uDisp > 0.0) {
      // Border taper (defect hunt D1 / user 2026-07-04 "straight lines bend near
      // the edges"): displacement must reach EXACTLY zero with ZERO GRADIENT
      // before the visible edge, else the tap ramp warps straight structures
      // (the e4645 light beam, a horizon) as they enter the border. A function
      // that is 0 at the border AND has zero gradient in a neighbourhood of it
      // must be identically 0 there — hence a RIGID-ZERO outer band [0,uEdge0]
      // (no gradient ⇒ no differential parallax ⇒ lines stay perfectly straight
      // where they meet the gold), then a gentle C1 smoothstep ramp to full over
      // [uEdge0,uEdge1]. Widened from the old 3%/9% (bent at the 0.25 orbit) to
      // ~9%/24% so the unavoidable ramp curvature is gentle and pushed away from
      // the frame edge where it reads as depth, not a kink.
      // Per-axis taper. With OVERSCAN (uOvs>0) the ramp runs from a rigid-zero
      // band at the mesh outer edge up to FULL at the cover edge (uv==uOvs), so
      // the whole ramp is hidden under the mat and every visible pixel is at
      // tap=1. Without overscan (ldi) it is the original interior [uEdge0,uEdge1]
      // taper on both axes — byte-identical.
      float e0x = uOvsX > 0.0 ? uOvsX * 0.35 : uEdge0;
      float e1x = uOvsX > 0.0 ? uOvsX        : uEdge1;
      float e0y = uOvsY > 0.0 ? uOvsY * 0.35 : uEdge0;
      float e1y = uOvsY > 0.0 ? uOvsY        : uEdge1;
      float tap = smoothstep(e0x, e1x, uv.x) * (1.0 - smoothstep(1.0 - e1x, 1.0 - e0x, uv.x))
                * smoothstep(e0y, e1y, uv.y) * (1.0 - smoothstep(1.0 - e1y, 1.0 - e0y, uv.y));
      // BIPOLAR displacement about the plate (user review 2026-07-02: "the
      // scenery should feel like it has depth as well"): near field rises
      // toward the subject, far field recedes BEYOND the plate — double the
      // internal scene depth of near-only lifting, same rest registration.
      // Soft-clamp the signed depth (defect hunt D3): linear to the ±0.35 knee
      // (≈ the fleet's 85th percentile of |depth−0.5|), 4:1 compressed beyond,
      // so bright/far outliers (the bbd5 moon) stop smearing at orbit extremes.
      float dep = texture2D(uDispMap, cuv).r - 0.5;
      // soft-clamp the SUBJECT peak first (tame the centred hotspot), THEN add the
      // scene ground-plane so the whole frame gets even parallax the flat depth lacks.
      float mag = abs(dep);
      mag = mag <= uKnee ? mag : uKnee + (mag - uKnee) * uKneeComp;
      float depC = sign(dep) * mag;
      depC += (0.5 - cuv.y) * uSceneTilt; // top→far, bottom→near ground-plane tilt
      // Plant ALL FOUR visible edges: near-foreground content at an edge (the bottom
      // cobblestone, or a side stone ledge) has uneven depth, so under tilt its ray-comp
      // shrinks it INWARD and the art edge RETREATS from the gold, opening a black void —
      // OR shears/overlaps the gold (user 2026-07-05: night-moon/night-explorer void on the
      // LEFT/RIGHT edges under horizontal tilt; earlier the same on the bottom). Fade total
      // displacement to 0 across the outer ~9% of BOTH axes so every edge stays pinned to the
      // frame plane (no retreat, no spill), while the interior keeps full depth. Originally
      // this pinned only Y (top/bottom); the side voids proved it must pin X too.
      // Keep the band NARROW (0.09): a wider ramp puts a visible displacement GRADIENT inside
      // the opening and BENDS lines that cross it (user 2026-07-05). A vertical line at the
      // left edge has constant plantX along its length → no bend; only content straddling the
      // outer 9% is affected, which is the frame-adjacent margin.
      float plantY = smoothstep(0.0, 0.09, cuv.y) * (1.0 - smoothstep(0.91, 1.0, cuv.y));
      float plantX = smoothstep(0.0, 0.09, cuv.x) * (1.0 - smoothstep(0.91, 1.0, cuv.x));
      float vplant = plantX * plantY;
      float d = depC * uDisp * tap * vplant; // signed world units
      p.z += d / uZScale;
      p.xy *= (uCamDist - d) / uCamDist; // ray compensation: rest-identical
    }
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;

/* FG cutout vertex. Phase 2 micro-relief, DOUBLY gated: zero-mean depth about the
   matte median, masked by smoothstep(0.6, 0.9, alpha) so the silhouette never
   stretches, and only enabled at all when meta.depthStructure ≥ 0.28. The same
   8% UV taper keeps edge-touching subjects from poking past the mat ring. */
const SP_FG_VERT = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;     // fg RGBA (alpha gates the relief)
  uniform sampler2D uDispMap; // fgdepth
  uniform float uDisp;        // 0 unless poster treatment (scene subjects stay rigid)
  uniform float uMedian;      // matte-median depth → zero-mean relief
  uniform float uEdgeRecede;  // world z to push the silhouette RIM toward the bg
  uniform float uCamDist;
  uniform float uZScale;
  varying vec2 vUv;
  void main(){
    vUv = uv;
    vec3 p = position;
    float tap = smoothstep(0.0, 0.08, uv.x) * (1.0 - smoothstep(0.92, 1.0, uv.x))
              * smoothstep(0.0, 0.08, uv.y) * (1.0 - smoothstep(0.92, 1.0, uv.y));
    float a = texture2D(uMap, uv).a;
    float d = 0.0;
    if (uDisp > 0.0) {
      float gate = smoothstep(0.6, 0.9, a);
      d += (texture2D(uDispMap, uv).r - uMedian) * uDisp * gate * tap;
    }
    // EDGE RECEDE (user 2026-07-04: the layered "see behind" is loved, but the
    // crisp silhouette read as a hard motion LINE / detachment as it slid over
    // the bg). Push ONLY the low-alpha RIM back toward the bg plane, so across
    // the soft alpha band the parallax rate blends from body-rate to bg-rate —
    // the boundary reads as a rounded 3D edge, not a cut, while the body stays
    // forward and still reveals behind. Ray-compensated ⇒ the silhouette shape
    // is IDENTICAL at rest; only the RIM (not the body) moves, so the character
    // never deforms (unlike the depth-matched contact anchor removed 07-02).
    if (uEdgeRecede > 0.0) {
      // Wider blend band (0.08..0.82) so the body→rim motion transition is more
      // gradual — the seam softens across a larger fraction of the alpha ramp.
      float rim = 1.0 - smoothstep(0.08, 0.82, a);
      d -= rim * uEdgeRecede * tap;
    }
    if (d != 0.0) {
      p.z += d / uZScale;
      p.xy *= (uCamDist - d) / uCamDist; // ray compensation: rest-identical
    }
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;

/* Synthetic parallax shadow for poster-flat plates (meta.bgTexture ≈ 0): a
   blurred dark copy of the fg silhouette living at the BG plane, offset like a
   soft key-light shadow. Because it sits at bg depth it parallaxes against the
   anchored subject — the only reliable depth cue over a featureless ground. */
const SP_SHADOW_FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;   // fg RGBA (premultiplied — alpha channel used)
  uniform vec2 uOffset;     // shadow offset in uv
  uniform vec2 uBlur;       // blur tap radius in uv
  uniform float uOpacity;
  varying vec2 vUv;
  void main(){
    vec2 c = vUv - uOffset;
    float a = 0.0;
    for (int i = -2; i <= 2; i++) {
      for (int j = -2; j <= 2; j++) {
        a += texture2D(uMap, c + vec2(float(i), float(j)) * uBlur).a;
      }
    }
    a /= 25.0;
    gl_FragColor = vec4(0.0, 0.0, 0.0, a * uOpacity);
  }
`;

/* BG plate fragment: OPAQUE clean plate, faint clamped relight [0.94, 1.10]. */
const SP_BG_FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;
  uniform sampler2D uDepth;
  uniform vec2 uTexel;
  uniform vec3 uLight;
  uniform float uRelief;
  varying vec2 vUv;
  ${SP_RELIGHT}
  void main(){
    gl_FragColor = vec4(texture2D(uMap, vUv).rgb * relightMul(uDepth, vUv, uTexel, uRelief, uLight, 0.94, 1.10), 1.0);
  }
`;

/* FG cutout fragment. The texture is premultiplied at GPU upload
   (texture.premultiplyAlpha = true) and composited with
   CustomBlending(ONE, ONE_MINUS_SRC_ALPHA) — clean soft-matte edges, no fringe,
   no alphaTest. Clamped relight [0.88, 1.22]. */
const SP_FG_FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;
  uniform sampler2D uDepth;
  uniform vec2 uTexel;
  uniform vec3 uLight;
  uniform float uRelief;
  varying vec2 vUv;
  ${SP_RELIGHT}
  void main(){
    vec4 c = texture2D(uMap, vUv); // premultiplied at upload
    float m = relightMul(uDepth, vUv, uTexel, uRelief, uLight, 0.88, 1.22);
    // Silhouette guard: fade relight to neutral at the cutout edge. The depth
    // cliff at the silhouette otherwise clamps the multiplier into a solid
    // dark/bright rim tracking the subject (the historical choppy-outline
    // bug, user-caught again 2026-07-02).
    m = mix(1.0, m, smoothstep(0.55, 0.9, c.a));
    gl_FragColor = vec4(c.rgb * m, c.a);
  }
`;

/* SOLO fragment: the cover verbatim, relit by the crisp whole-cover depth.
   Same shape as SP_BG_FRAG but with a slightly wider clamp [0.90, 1.15] — the
   relight is solo's supporting depth cue (with the displacement parallax), and
   there is no subject cutout whose silhouette could ring. Kept as a separate
   shader so the ldi bg path stays byte-identical. */
const SP_SOLO_FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;
  uniform sampler2D uDepth;
  uniform vec2 uTexel;
  uniform vec3 uLight;
  uniform float uRelief;
  varying vec2 vUv;
  ${SP_RELIGHT}
  void main(){
    gl_FragColor = vec4(texture2D(uMap, vUv).rgb * relightMul(uDepth, vUv, uTexel, uRelief, uLight, 0.90, 1.15), 1.0);
  }
`;

/* Flat print: raw passthrough of the cover — byte-identical at rest; the spatial
   feel comes from the whole-card tilt + the gold's moving key light. */
const FLAT_VERT = /* glsl */ `
  varying vec2 vUv;
  void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const FLAT_FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;
  varying vec2 vUv;
  void main(){ gl_FragColor = vec4(texture2D(uMap, vUv).rgb, 1.0); }
`;

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}

/* ── spatial2 manifest gate ───────────────────────────────────────────────── */

interface LdiAssets {
  kind: 'ldi';
  fg: HTMLImageElement;
  bg: HTMLImageElement;
  fgDepth: HTMLImageElement | null;
  bgDepth: HTMLImageElement | null;
  depthStructure: number;
  /** Plate structure outside the subject (0 = featureless poster ground). */
  bgTexture: number;
  /** Phase-2 relief flag (URL param `?relief=1` or localStorage `spatial2Relief`). */
  relief: boolean;
}

interface SoloAssets {
  kind: 'solo';
  /** The cover verbatim (solo.png). */
  solo: HTMLImageElement;
  /** Whole-cover bilateral-median depth (solodepth.png) — REQUIRED: without it
   *  solo has no depth cue at all, so a missing map fails closed to flat. */
  soloDepth: HTMLImageElement;
  /** Authoritative content-adaptive relief scale from the pipeline
   *  (meta.soloReliefScale ∈ [0,1]); undefined → the renderer computes it from
   *  the cover at load (graphicReliefScale). */
  reliefScale: number | undefined;
  /** Phase-2 relief flag (URL param `?relief=1` or localStorage `spatial2Relief`). */
  relief: boolean;
}

type SpatialAssets = LdiAssets | SoloAssets;

/** Phase-2 displacement is the SHIPPING DEFAULT (2026-07-05). It was DEFAULT OFF
 *  behind ?relief=1 while being verified — but that flag was only ever set on the
 *  /spatial2-test harness, so the live gallery/corridor/downloads rendered FLAT (the
 *  camera orbited an undisplaced mesh → "circles flat on one plane", user 2026-07-05).
 *  Now default ON everywhere; opt OUT with ?relief=0 (or localStorage spatial2Relief='0')
 *  for the rest-identity CI gate. Rest pose is byte-identical either way (ray-comp). */
function reliefEnabled(): boolean {
  try {
    const p = new URLSearchParams(window.location.search).get('relief');
    if (p === '0') return false;
    if (p === '1') return true;
    return window.localStorage.getItem('spatial2Relief') !== '0';
  } catch {
    return true;
  }
}

/**
 * The ONE gating fetch: `<spatialUrl>/meta.json`. A well-formed v2 manifest
 * with mode "ldi" unlocks the 2-layer spatial scene; mode "solo" unlocks the
 * continuous-mesh solo scene; anything else (404, version mismatch, network
 * error, texture failure) resolves null → the caller renders the premium FLAT
 * presentation. No HEAD probes, no URL derivation from other asset paths.
 */
async function loadSpatial2(base: string): Promise<SpatialAssets | null> {
  try {
    const res = await fetch(`${base}/meta.json`);
    if (!res.ok) return null;
    const j: unknown = await res.json();
    if (typeof j !== 'object' || j === null) return null;
    const meta = j as {
      v?: unknown;
      mode?: unknown;
      depthStructure?: unknown;
      bgTexture?: unknown;
      soloReliefScale?: unknown;
    };
    if (meta.v !== 2) return null;
    if (meta.mode === 'solo') {
      // Both assets are load-bearing: a reject on either fails closed → flat.
      const [solo, soloDepth] = await Promise.all([
        loadImg(`${base}/solo.png`),
        loadImg(`${base}/solodepth.png`),
      ]);
      return {
        kind: 'solo',
        solo,
        soloDepth,
        reliefScale: typeof meta.soloReliefScale === 'number' ? meta.soloReliefScale : undefined,
        relief: reliefEnabled(),
      };
    }
    if (meta.mode !== 'ldi') return null;
    const [fg, bg, fgDepth, bgDepth] = await Promise.all([
      loadImg(`${base}/fg.png`),
      loadImg(`${base}/bg.png`),
      loadImg(`${base}/fgdepth.png`).catch(() => null),
      loadImg(`${base}/bgdepth.png`).catch(() => null),
    ]);
    return {
      kind: 'ldi',
      fg,
      bg,
      fgDepth,
      bgDepth,
      depthStructure: typeof meta.depthStructure === 'number' ? meta.depthStructure : 0,
      // Old bundles without the metric default HIGH (no poster treatment).
      bgTexture: typeof meta.bgTexture === 'number' ? meta.bgTexture : 1,
      relief: reliefEnabled(),
    };
  } catch {
    return null; // fail closed → premium flat
  }
}

/** Median fgdepth over the matte (alpha ≥ 0.5) — centres the Phase-2 micro-relief
 *  so the subject breathes about its own body instead of lifting wholesale. */
function matteMedianDepth(fg: HTMLImageElement, depth: HTMLImageElement | HTMLCanvasElement): number {
  try {
    const W = 128;
    const H = Math.max(1, Math.round(W / (fg.naturalWidth / fg.naturalHeight)));
    const draw = (img: HTMLImageElement | HTMLCanvasElement): Uint8ClampedArray | null => {
      const c = document.createElement('canvas');
      c.width = W;
      c.height = H;
      const x = c.getContext('2d');
      if (!x) return null;
      x.drawImage(img, 0, 0, W, H);
      return x.getImageData(0, 0, W, H).data;
    };
    const a = draw(fg);
    const d = draw(depth);
    if (!a || !d) return 0.5;
    const vals: number[] = [];
    for (let i = 0; i < W * H; i += 1) {
      if (a[i * 4 + 3]! >= 128) vals.push(d[i * 4]!);
    }
    if (vals.length === 0) return 0.5;
    vals.sort((p, q) => p - q);
    return vals[Math.floor(vals.length / 2)]! / 255;
  } catch {
    return 0.5;
  }
}

/** Content-adaptive SOLO relief score (FIX 2) ∈ [FLOOR, 1]. A flat GRAPHIC
 *  poster (bold text + hard ink/vector edges bounding large flat fills) must get
 *  MINIMAL displacement so its straight letters/lines don't warp under the
 *  depth relief; a photoreal SCENE keeps full depth. Signal: the STRONG-EDGE
 *  FRACTION after an aggressive de-halftone downsample (~72px long side) — the
 *  bold ink outlines + letter strokes survive and read as a large fraction of
 *  strong gradients, while photographic texture/grain averages out to soft
 *  gradients. Calibrated on the fleet: heavy comics ≈ 0.30–0.34, flat vector
 *  ≈ 0.18, photoreal scenes ≈ 0.02–0.04 (probe_graphic.py). Deterministic and
 *  cheap (one 72px canvas read at load); the pipeline may also pin an
 *  authoritative value in meta.soloReliefScale, which overrides this. */
function graphicReliefScale(img: HTMLImageElement): number {
  try {
    const L = 72;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const s = L / Math.max(w, h);
    const cw = Math.max(1, Math.round(w * s));
    const ch = Math.max(1, Math.round(h * s));
    const c = document.createElement('canvas');
    c.width = cw;
    c.height = ch;
    const x = c.getContext('2d', { willReadFrequently: true });
    if (!x) return 1;
    x.imageSmoothingEnabled = true;
    x.imageSmoothingQuality = 'high';
    x.drawImage(img, 0, 0, cw, ch);
    const d = x.getImageData(0, 0, cw, ch).data;
    const gray = new Float32Array(cw * ch);
    for (let i = 0; i < cw * ch; i += 1) gray[i] = (d[i * 4]! + d[i * 4 + 1]! + d[i * 4 + 2]!) / 3;
    let strong = 0;
    for (let y = 0; y < ch; y += 1) {
      for (let xx = 0; xx < cw; xx += 1) {
        const i = y * cw + xx;
        const gx = xx > 0 ? Math.abs(gray[i]! - gray[i - 1]!) : 0;
        const gy = y > 0 ? Math.abs(gray[i]! - gray[i - cw]!) : 0;
        if (Math.max(gx, gy) > 45) strong += 1;
      }
    }
    const se = strong / (cw * ch);
    const t = Math.min(1, Math.max(0, (se - RELIEF_EDGE_LO) / (RELIEF_EDGE_HI - RELIEF_EDGE_LO)));
    return Math.min(1, Math.max(RELIEF_SCALE_FLOOR, 1 - t * RELIEF_REDUCTION));
  } catch {
    return 1; // fail open → full depth (never over-flatten on an error)
  }
}

/* ── texture helpers ──────────────────────────────────────────────────────── */

/** Content texture: NoColorSpace (raw display-space passthrough — an SRGB decode
 *  darkened the bg 2× once; never again), ClampToEdge (content never wraps or
 *  mirrors), anisotropic + mipmapped for crispness under the orbit. */
function contentTex(img: HTMLImageElement, renderer: THREE.WebGLRenderer, premultiply: boolean): THREE.Texture {
  const t = new THREE.Texture(img);
  t.colorSpace = THREE.NoColorSpace;
  t.anisotropy = renderer.capabilities.getMaxAnisotropy();
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.premultiplyAlpha = premultiply;
  t.needsUpdate = true;
  return t;
}

/** Heavily-smoothed depth copy for VERTEX displacement — body-scale volume
 *  only. The bilateral-filtered fgdepth keeps sharp cliffs (right for
 *  relight); displacing by those cliffs jellies the subject. A 192px + 5px
 *  blur copy keeps only the low-frequency form, so the relief reads as solid
 *  volume. Returns the canvas too: the zero-mean centring (uMedian) MUST be
 *  computed over this SAME smoothed map — a median from the raw fgdepth left
 *  a nonzero mean displacement (the b4b02 bottom-arc registration drift). */
function smoothDepthTex(img: HTMLImageElement | null): { tex: THREE.Texture; canvas: HTMLCanvasElement | null } {
  if (!img) return { tex: depthDataTex(null), canvas: null };
  const w = 192;
  const h = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * w));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d');
  if (!x) return { tex: depthDataTex(img), canvas: null };
  x.filter = 'blur(5px)';
  x.drawImage(img, 0, 0, w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return { tex: t, canvas: c };
}

/** BG DISPLACEMENT map (defect hunt D2 — the "breathing band"): the raw
 *  bgdepth carries a depth cliff exactly along the erased-subject boundary, so
 *  vertex displacement deformed the inpainted fill band ~2× the far field and
 *  folded triangles at strong cliffs. This bakes a displacement-only copy where
 *  the matte-adjacent zone (~4% of the plate width) is blended toward a
 *  heavily-blurred LOCAL MEDIAN of the plate depth — the fill band moves with
 *  its neighbourhood instead of "breathing" against it — plus a mild global
 *  smoothing at vertex-sampling scale so single-texel spikes can't fold the
 *  96×96 mesh. Relight keeps sampling the crisp bgdepth (uDepth), not this.
 *  SOLO passes `fg = null` (no matte exists): only the global anti-fold
 *  smoothing applies — the whole-cover depth has no erased-subject cliff. */
function bgDispTex(bgDepth: HTMLImageElement | null, fg: HTMLImageElement | null): THREE.Texture {
  if (!bgDepth) return depthDataTex(null);
  const W = 256;
  const H = Math.max(1, Math.round((bgDepth.naturalHeight / bgDepth.naturalWidth) * W));
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  if (!ctx) return depthDataTex(bgDepth);
  const grab = (img: HTMLImageElement, channel: number): Float32Array => {
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);
    const d = ctx.getImageData(0, 0, W, H).data;
    const out = new Float32Array(W * H);
    for (let i = 0; i < W * H; i += 1) out[i] = d[i * 4 + channel]!;
    return out;
  };
  // Edge-CLAMPED separable box blur ×passes (≈ gaussian). Canvas filter blur is
  // not used because it bleeds transparent black in at the borders.
  const blur = (src: Float32Array, r: number, passes: number): Float32Array => {
    const a = Float32Array.from(src);
    const b = new Float32Array(W * H);
    for (let p = 0; p < passes; p += 1) {
      for (let y = 0; y < H; y += 1)
        for (let x = 0; x < W; x += 1) {
          let s = 0;
          for (let k = -r; k <= r; k += 1) s += a[y * W + Math.min(W - 1, Math.max(0, x + k))]!;
          b[y * W + x] = s / (2 * r + 1);
        }
      for (let x = 0; x < W; x += 1)
        for (let y = 0; y < H; y += 1) {
          let s = 0;
          for (let k = -r; k <= r; k += 1) s += b[Math.min(H - 1, Math.max(0, y + k)) * W + x]!;
          a[y * W + x] = s / (2 * r + 1);
        }
    }
    return a;
  };
  const depth = grab(bgDepth, 0);
  const smooth = blur(depth, 2, 1); // mild anti-fold smoothing everywhere
  const local = blur(depth, 8, 3); // ≈ local median of the plate depth
  const prox = fg ? blur(grab(fg, 3), 10, 2) : null; // matte proximity (fg alpha, blurred)
  const img = ctx.createImageData(W, H);
  for (let i = 0; i < W * H; i += 1) {
    const w = prox ? Math.min(1, (prox[i]! / 255) * 1.5) : 0;
    const v = Math.max(0, Math.min(255, smooth[i]! + (local[i]! - smooth[i]!) * w));
    const o = i * 4;
    img.data[o] = v;
    img.data[o + 1] = v;
    img.data[o + 2] = v;
    img.data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
}

/** Depth texture (relight normals + Phase-2 displacement). A missing depth map
 *  degrades to a neutral 1×1 grey: zero gradient → relight multiplier exactly 1
 *  and zero displacement — never a broken render. */
function depthDataTex(img: HTMLImageElement | null): THREE.Texture {
  if (!img) {
    const t = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1);
    t.needsUpdate = true;
    return t;
  }
  const t = new THREE.Texture(img);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.needsUpdate = true;
  return t;
}

/** Normal map for the gold molding from its luminance (frame shimmer/relight). */
function frameNormalTex(img: HTMLImageElement): THREE.CanvasTexture {
  const a = img.naturalWidth / img.naturalHeight;
  const DW = Math.min(256, img.naturalWidth);
  const DH = Math.max(1, Math.round(DW / a));
  const c = document.createElement('canvas');
  c.width = DW;
  c.height = DH;
  const x = c.getContext('2d');
  if (!x) throw new Error('no 2d ctx');
  x.drawImage(img, 0, 0, DW, DH);
  const raw = x.getImageData(0, 0, DW, DH).data;
  const g = new Float32Array(DW * DH);
  for (let i = 0; i < DW * DH; i += 1) {
    g[i] = (0.299 * raw[i * 4]! + 0.587 * raw[i * 4 + 1]! + 0.114 * raw[i * 4 + 2]!) / 255;
  }
  const bl = new Float32Array(DW * DH);
  const R = 3;
  for (let y = 0; y < DH; y += 1)
    for (let xx = 0; xx < DW; xx += 1) {
      let s = 0;
      let n = 0;
      for (let j = -R; j <= R; j += 1)
        for (let i = -R; i <= R; i += 1) {
          const ax = xx + i;
          const ay = y + j;
          if (ax < 0 || ay < 0 || ax >= DW || ay >= DH) continue;
          s += g[ay * DW + ax]!;
          n += 1;
        }
      bl[y * DW + xx] = s / n;
    }
  const nC = document.createElement('canvas');
  nC.width = DW;
  nC.height = DH;
  const nctx = nC.getContext('2d')!;
  const nI = nctx.createImageData(DW, DH);
  const str = 3.0;
  for (let y = 0; y < DH; y += 1)
    for (let xx = 0; xx < DW; xx += 1) {
      const gx = (bl[y * DW + Math.min(DW - 1, xx + 1)]! - bl[y * DW + Math.max(0, xx - 1)]!) * str;
      const gy = (bl[Math.min(DH - 1, y + 1) * DW + xx]! - bl[Math.max(0, y - 1) * DW + xx]!) * str;
      let ax = -gx;
      let ay = gy;
      let az = 1;
      const l = Math.hypot(ax, ay, az);
      ax /= l;
      ay /= l;
      az /= l;
      const o = (y * DW + xx) * 4;
      nI.data[o] = (ax * 0.5 + 0.5) * 255;
      nI.data[o + 1] = (ay * 0.5 + 0.5) * 255;
      nI.data[o + 2] = (az * 0.5 + 0.5) * 255;
      nI.data[o + 3] = 255;
    }
  nctx.putImageData(nI, 0, 0);
  return new THREE.CanvasTexture(nC);
}

/** Renders a deterministic orbit offscreen at export resolution, handing each
 *  finished sRGB RGBA frame (top-down) to `onFrame` to encode. `ampX`/`ampY` are
 *  NORMALIZED drive amplitudes (1 = the full live ambient orbit); the component
 *  maps drive → camera-orbit world amps (spatial) or whole-card tilt (flat), so
 *  exports move in lockstep with the live preview for BOTH modes. */
export type CaptureClip = (opts: {
  width: number;
  height: number;
  frames: number;
  ampX?: number;
  ampY?: number;
  watermark?: boolean;
  onFrame: (rgba: Uint8ClampedArray, w: number, h: number, i: number) => void | Promise<void>;
}) => Promise<void>;

interface SculptApi {
  dispose: () => void;
  enableMotion: () => void;
  captureClip: CaptureClip;
  /** Park the render loop (keeps the WebGL context warm) when offscreen, so
   *  several sculpts can coexist on a page with only the visible one drawing. */
  setPaused: (paused: boolean) => void;
  /** Toggle the bold, download-matching auto-orbit at runtime (the lightbox's
   *  "auto play"). On overrides pointer/gyro; off returns to idle/tilt. */
  setAmbient: (on: boolean) => void;
}

/** Imperative controls the live scene hands back to a host (e.g. the lightbox)
 *  so it can render its own tilt / auto-play affordances outside the canvas. */
export interface SculptControls {
  enableMotion: () => void;
  setAmbient: (on: boolean) => void;
}

/** A gold-leaf "Gilding.app" wordmark as a textured plane, in the brand's splash
 *  foil (Fraunces serif + bright-centre gold gradient + soft emboss). Returned as
 *  a mesh to parent into the content group so it reads 3D and un-croppable. */
function buildWatermark(
  renderer: THREE.WebGLRenderer,
  ow: number,
  oh: number,
): { mesh: THREE.Mesh; dispose: () => void } {
  const text = 'Gilding.app';
  const SS = 4; // supersample for crisp glyphs
  const fpx = 96 * SS;
  const meas = document.createElement('canvas').getContext('2d');
  let tw = fpx * 5;
  if (meas) {
    meas.font = `700 ${fpx}px Fraunces, Georgia, serif`;
    tw = Math.ceil(meas.measureText(text).width);
  }
  const padX = Math.round(fpx * 0.16);
  const padTop = Math.round(fpx * 0.3);
  const padBot = Math.round(fpx * 0.42); // descender (g/p) + shadow room
  const cw = tw + padX * 2;
  const ch = fpx + padTop + padBot;
  const cv = document.createElement('canvas');
  cv.width = cw;
  cv.height = ch;
  const ctx = cv.getContext('2d');
  if (ctx) {
    ctx.font = `700 ${fpx}px Fraunces, Georgia, serif`;
    ctx.textBaseline = 'alphabetic';
    const baseX = padX;
    const baseY = padTop + fpx;
    const g = ctx.createLinearGradient(baseX, 0, baseX + tw, 0);
    g.addColorStop(0, '#8a6320');
    g.addColorStop(0.28, '#c2922f');
    g.addColorStop(0.42, '#e9c873');
    g.addColorStop(0.52, '#fff6da');
    g.addColorStop(0.62, '#e9c873');
    g.addColorStop(0.76, '#b9842a');
    g.addColorStop(1, '#8a6320');
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = fpx * 0.1;
    ctx.shadowOffsetY = fpx * 0.05;
    ctx.fillStyle = g;
    ctx.fillText(text, baseX, baseY);
    ctx.restore();
    // a faint upper highlight (only over the glyphs) for a leafed, embossed edge
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = 'rgba(255,250,235,0.1)';
    ctx.fillRect(0, 0, cw, ch * 0.5);
    ctx.globalCompositeOperation = 'source-over';
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  const aspect = cw / ch;
  const wmH = oh * 0.072; // ~7% of the picture height
  const wmW = wmH * aspect;
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    toneMapped: false, // keep the foil bright/accurate, not ACES-darkened
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(wmW, wmH), mat);
  const inset = oh * 0.05;
  mesh.position.set(ow / 2 - inset - wmW / 2, -oh / 2 + inset + wmH / 2, 0.08);
  mesh.renderOrder = 25;
  return {
    mesh,
    dispose: () => {
      mesh.geometry.dispose();
      tex.dispose();
      mat.dispose();
    },
  };
}

function buildScene(
  holder: HTMLDivElement,
  art: HTMLImageElement,
  frm: HTMLImageElement,
  spatial: SpatialAssets | null,
  ambient: boolean,
): SculptApi {
  // "spatial" = 2-layer ldi (the historical harness name), "solo" = continuous
  // mesh, "flat" = the degrade-only framed print. Reported via data-sculpt-mode
  // + __sculptDiag.mode so Playwright/harness can distinguish all three.
  const mode: 'spatial' | 'solo' | 'flat' =
    spatial === null ? 'flat' : spatial.kind === 'solo' ? 'solo' : 'spatial';
  // preserveDrawingBuffer so a motion-clip export can read the rendered canvas
  // after drawing (the screen path is colour-faithful; a render target double-
  // encodes sRGB and the clip came out milky/faded).
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
  const r0 = holder.getBoundingClientRect();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(r0.width, r0.height);
  // ACES + sRGB output serve the FRAME's PBR gold. Content bypasses both (raw
  // ShaderMaterials + NoColorSpace textures) so it stays byte-comparable.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  renderer.domElement.style.display = 'block';
  holder.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const cam = new THREE.PerspectiveCamera(28, r0.width / r0.height, 0.1, 30);
  cam.position.set(0, 0, CAM);
  cam.lookAt(0, 0, 0);

  // FILL the canvas with the frame: scale the whole group so the frame's outer
  // edge meets the canvas edge. ORBIT_SCALE damps the drive on long aspects (a
  // 16:9 sweep at full amplitude reads as nauseating); FILL_OVERSCAN leaves the
  // margin that absorbs the orbit/tilt excursion.
  const visibleH = 2 * CAM * Math.tan((28 * Math.PI) / 180 / 2);
  const aImg = art.naturalWidth / art.naturalHeight;
  const longAx = Math.max(aImg, 1 / aImg);
  const aspectDeviation = Math.min(1, (longAx - 1) / 0.78); // 0 at sq, 1 at 16:9 / 9:16
  const ORBIT_SCALE = 1 / (0.5 + 0.5 * longAx);
  const FILL_OVERSCAN = 0.96 - 0.08 * aspectDeviation;
  const FILL = (visibleH / (FRAME_OPEN_H + 2 * FRAME_OUTER)) * FILL_OVERSCAN;

  // studio environment for metallic reflections
  const ec = document.createElement('canvas');
  ec.width = 1024;
  ec.height = 512;
  const ex = ec.getContext('2d')!;
  const gr = ex.createLinearGradient(0, 0, 0, 512);
  gr.addColorStop(0, '#2b2535');
  gr.addColorStop(0.42, '#1a1622');
  gr.addColorStop(0.5, '#3d3344');
  gr.addColorStop(1, '#070509');
  ex.fillStyle = gr;
  ex.fillRect(0, 0, 1024, 512);
  let rg = ex.createRadialGradient(360, 150, 0, 360, 150, 260);
  rg.addColorStop(0, '#fff3d6');
  rg.addColorStop(0.5, 'rgba(255,225,170,.5)');
  rg.addColorStop(1, 'rgba(0,0,0,0)');
  ex.fillStyle = rg;
  ex.fillRect(0, 0, 1024, 512);
  rg = ex.createRadialGradient(770, 210, 0, 770, 210, 200);
  rg.addColorStop(0, 'rgba(155,180,255,.5)');
  rg.addColorStop(1, 'rgba(0,0,0,0)');
  ex.fillStyle = rg;
  ex.fillRect(0, 0, 1024, 512);
  const envTex = new THREE.CanvasTexture(ec);
  envTex.mapping = THREE.EquirectangularReflectionMapping;
  envTex.colorSpace = THREE.SRGBColorSpace;
  scene.environment = envTex;

  // pivot rotates ONLY in flat mode (the rigid whole-card tilt). In spatial mode
  // planes NEVER translate or rotate — all motion is the camera orbit.
  const pivot = new THREE.Group();
  scene.add(pivot);
  const group = new THREE.Group();
  pivot.add(group);
  group.scale.setScalar(FILL);

  // Opening = the gilding rectangle, CENTERED at origin; height fixed, width
  // follows the aspect — the 9-slice wraps it exactly at any aspect.
  const oh = FRAME_OPEN_H;
  const ow = oh * aImg;

  const uLightDir = new THREE.Vector3(0, 0, 1);
  const sharedLight = { value: uLightDir };
  const disposables: { dispose: () => void }[] = [];
  // ── REST FIDELITY (defect hunt C1) ── uniforms whose effects are only
  // APPROXIMATELY rest-neutral through the discretized mesh (depth
  // displacement interpolates between ray-compensated vertices; the poster
  // shadow is synthetic) are driven per-frame as base × poseGain, so the rest
  // frame is MATHEMATICALLY the cover render and the effects fade in with
  // motion. Ambient orbit + clip exports run at |pose| ≈ 1 → gain 1.
  const poseGained: { u: { value: number }; base: number }[] = [];
  const poseGain = (sx: number, sy: number): number => {
    const t = Math.min(1, Math.max(0, (Math.hypot(sx, sy) - 0.05) / 0.25));
    return t * t * (3 - 2 * t); // smoothstep(0.05, 0.3, |pose|)
  };

  // Diagnostics for the verification harness (Playwright reads these).
  const diag: Record<string, unknown> = { mode, fill: FILL, orbitScale: ORBIT_SCALE };

  if (spatial && spatial.kind === 'ldi') {
    // ── SPATIAL: server-validated 2-layer LDI ──────────────────────────────
    const fgTex = contentTex(spatial.fg, renderer, true);
    const bgTex = contentTex(spatial.bg, renderer, false);
    const fgDepthTex = depthDataTex(spatial.fgDepth);
    const fgSmooth = smoothDepthTex(spatial.fgDepth);
    const fgDepthSmooth = fgSmooth.tex;
    const bgDepthTex = depthDataTex(spatial.bgDepth);
    const bgDispMap = bgDispTex(spatial.bgDepth, spatial.fg);
    disposables.push(fgTex, bgTex, fgDepthTex, fgDepthSmooth, bgDepthTex, bgDispMap);
    const texel = new THREE.Vector2(2 / 1024, 2 / 1024);

    const bgUniforms = {
      uMap: { value: bgTex },
      uDepth: { value: bgDepthTex },
      uDispMap: { value: bgDispMap },
      uDisp: { value: 0 },
      uCamDist: { value: CAM + SEP },
      uZScale: { value: FILL },
      uEdge0: { value: TAP_EDGE0 },
      uEdge1: { value: TAP_EDGE1 },
      // LDI bg plate keeps the original clamp (LDI motion is unchanged — user
      // 2026-07-04 is happy with it); only SOLO gets the softer center peak + tilt.
      uKnee: { value: 0.35 },
      uKneeComp: { value: 0.25 },
      uSceneTilt: { value: 0 },
      uOvsX: { value: 0 }, // ldi bg plate: NO overscan (unchanged behaviour)
      uOvsY: { value: 0 },
      uTexel: { value: texel },
      uLight: sharedLight,
      uRelief: { value: spatial.bgDepth ? RELIGHT_NORMAL_GAIN : 0 },
    };
    const bgMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(ow, oh, 96, 96),
      // toneMapped false is implicit for raw ShaderMaterials (no tonemapping
      // chunk) — set explicitly to document the byte-faithful content path.
      new THREE.ShaderMaterial({ uniforms: bgUniforms, vertexShader: SP_BG_VERT, fragmentShader: SP_BG_FRAG, toneMapped: false }),
    );
    bgMesh.position.set(0, 0, -SEP);
    bgMesh.renderOrder = 0;
    group.add(bgMesh);

    const fgUniforms = {
      uMap: { value: fgTex },
      uDepth: { value: fgDepthTex },
      uDispMap: { value: fgDepthSmooth },
      uDisp: { value: 0 },
      uMedian: { value: 0.5 },
      uEdgeRecede: { value: 0 },
      uCamDist: { value: CAM },
      uZScale: { value: FILL },
      uTexel: { value: texel },
      uLight: sharedLight,
      uRelief: { value: spatial.fgDepth ? RELIGHT_NORMAL_GAIN : 0 },
    };
    const fgMat = new THREE.ShaderMaterial({
      uniforms: fgUniforms,
      vertexShader: SP_FG_VERT,
      fragmentShader: SP_FG_FRAG,
      transparent: true,
      depthWrite: false, // never occludes; composites over the plate
      toneMapped: false,
    });
    // Premultiplied compositing: (ONE, ONE_MINUS_SRC_ALPHA) — the texture is
    // premultiplied at upload, the shader outputs premultiplied rgb.
    fgMat.blending = THREE.CustomBlending;
    fgMat.blendSrc = THREE.OneFactor;
    fgMat.blendDst = THREE.OneMinusSrcAlphaFactor;
    fgMat.blendSrcAlpha = THREE.OneFactor;
    fgMat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    // Posters lift the subject (the mascot) FAR forward of the badge/paper
    // plate so the CHARACTER pops OUT of the logo (user 2026-07-03) — not the
    // whole disc lifting as one. Safe because the reveal behind the mascot is
    // uniform paper, which the pipeline's scene-extension fills perfectly. The
    // badge ring + text live on the bg plate and stay put → real layering.
    const posterLift = spatial.relief && spatial.bgTexture < POSTER_BG_TEXTURE_MAX;
    const fgZ = posterLift ? POSTER_FG_Z : FG_Z;
    const fgMesh = new THREE.Mesh(new THREE.PlaneGeometry(ow, oh, 64, 64), fgMat);
    fgMesh.position.set(0, 0, -fgZ);
    fgMesh.renderOrder = 1;
    group.add(fgMesh);

    // ── REGISTRATION (the H2 fix) ── BOTH planes scale to project exactly onto
    // the OPENING plane (group origin) at rest: k = distCam(plane)/distCam(open),
    // COMPUTED WORLD-SPACE after the FILL transform (the group scale rescales
    // child z, so nominal values are wrong by construction — never hardcode).
    scene.updateMatrixWorld(true);
    const openW = group.getWorldPosition(new THREE.Vector3());
    const fgW = fgMesh.getWorldPosition(new THREE.Vector3());
    const bgW = bgMesh.getWorldPosition(new THREE.Vector3());
    const kFg = bgRegistrationScale(cam.position.z, openW.z, fgW.z);
    const k = bgRegistrationScale(cam.position.z, openW.z, bgW.z);
    fgMesh.scale.set(kFg, kFg, 1);
    bgMesh.scale.set(k, k, 1);
    const camDistFg = cam.position.z - fgW.z;
    const camDistBg = cam.position.z - bgW.z;
    fgUniforms.uCamDist.value = camDistFg;
    bgUniforms.uCamDist.value = camDistBg;
    // world units per geometry-local z (mesh z-scale is 1 on both planes)
    fgUniforms.uZScale.value = FILL;
    bgUniforms.uZScale.value = FILL;

    // ── Phase 2 (flag `spatial2Relief`, DEFAULT OFF) ── ray-compensated bgdepth
    // displacement + doubly-gated fg micro-relief.
    const bgReliefOn = spatial.relief && spatial.bgDepth !== null;
    const fgReliefOn =
      spatial.relief && spatial.fgDepth !== null && spatial.depthStructure >= FG_RELIEF_MIN_STRUCTURE;
    // Poster treatment: a featureless plate (meta.bgTexture ≈ 0) shows no
    // parallax, so the subject gets boosted relief + a synthetic parallax
    // shadow at the bg plane (the only readable depth cues over flat ground).
    const posterish = spatial.relief && spatial.bgTexture < POSTER_BG_TEXTURE_MAX;
    // Subject volume: displacement samples the SMOOTHED depth (body-scale form
    // only — sharp-cliff displacement jellied the subject, "hopping"). Scene
    // gens get gentle volume so the subject reads as a solid IN the scene, not
    // a flat sticker; posters get the boosted treatment. RELIEF IS CONSTANT
    // (NOT pose-gained): a real 3D object has stable volume, and the vertex
    // ray-compensation already makes displaced relief project to the cover at
    // the rest camera. Pose-gaining it (0 at rest → full when tilted) made the
    // subject INFLATE/DEFLATE as the view tilted — the "bouncing/float" tell
    // (user 2026-07-03). Constant volume moves proportionally with the scene.
    bgUniforms.uDisp.value = bgReliefOn ? D_BG : 0;
    fgUniforms.uDisp.value = fgReliefOn ? (posterish ? D_FG * POSTER_FG_RELIEF_BOOST : D_FG_SCENE) : 0;
    // Edge-recede: SCENE gens only (posters want the badge to stay a crisp
    // sticker). Push the rim ~85% of the way from the fg plane to the bg plane
    // so its parallax blends into the scene — the silhouette stops reading as a
    // hard cut line while the body keeps its see-behind separation.
    fgUniforms.uEdgeRecede.value = posterish ? 0 : (SEP - fgZ) * EDGE_RECEDE_FRAC;
    if (fgReliefOn && spatial.fgDepth) {
      // Median over the SAME smoothed map the vertex shader samples — the raw
      // fgdepth median left a nonzero mean displacement (b4b02 bottom-arc drift).
      fgUniforms.uMedian.value = matteMedianDepth(spatial.fg, fgSmooth.canvas ?? spatial.fgDepth);
    }

    if (posterish) {
      const shadowUniforms = {
        uMap: { value: fgTex },
        // Bigger offset + blur so the lifted-sticker separation reads on flat
        // graphic art (user 2026-07-03: poster "not 3D at all"). Deeper drop.
        uOffset: { value: new THREE.Vector2(0.024, -0.032) },
        uBlur: { value: new THREE.Vector2(0.013, 0.013 * aImg) },
        // Pose-gained (C1): the flat cream cover has NO shadow, so the rest
        // frame must not either — it fades in with the orbit, where the
        // parallax it exists to sell actually happens.
        uOpacity: { value: 0 },
      };
      poseGained.push({ u: shadowUniforms.uOpacity, base: 0.34 });
      const shadowMat = new THREE.ShaderMaterial({
        uniforms: shadowUniforms,
        vertexShader: FLAT_VERT,
        fragmentShader: SP_SHADOW_FRAG,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      });
      const shadowMesh = new THREE.Mesh(new THREE.PlaneGeometry(ow, oh, 1, 1), shadowMat);
      // Just in front of the bg plate: it parallaxes with the scenery, so the
      // anchored subject visibly floats above it.
      shadowMesh.position.set(0, 0, -SEP + 0.02);
      shadowMesh.renderOrder = 0.5;
      group.add(shadowMesh);
      scene.updateMatrixWorld(true);
      const shW = shadowMesh.getWorldPosition(new THREE.Vector3());
      // Register through the OPENING plane like fg/bg (C2 audit): the fg is
      // opening-registered, so the shadow silhouette must be too — the old
      // fgW.z reference left the shadow ~3.3% undersized vs the subject.
      const kSh = bgRegistrationScale(cam.position.z, openW.z, shW.z);
      shadowMesh.scale.set(kSh, kSh, 1);
    }

    Object.assign(diag, {
      k,
      camDistFg,
      camDistBg,
      sepWorld: fgW.z - bgW.z,
      depthStructure: spatial.depthStructure,
      relief: spatial.relief,
      bgReliefOn,
      fgReliefOn,
    });
  } else if (spatial) {
    // ── SOLO: ONE continuous full-frame mesh (the NO-FLAT floor) ───────────
    // The cover verbatim, SET BACK behind the opening and REGISTRATION-SCALED
    // exactly like the ldi bg plate (rest projects pixel-onto the opening),
    // displaced by the whole-cover depth through the SAME bipolar SP_BG_VERT
    // machinery: (depth−0.5)·D_SOLO with ray compensation (rest-identical) and
    // the ±knee soft-clamp (outliers can't swim). The set-back + overscan put the
    // mesh boundary + border taper BEHIND the mat/frame, so the visible opening
    // carries FULL, untapered displacement (edges move proportionately). NO
    // cutout, NO fill → zero disocclusion; the opaque mesh fills the opening.
    const soloTex = contentTex(spatial.solo, renderer, false);
    const soloDepthTex = depthDataTex(spatial.soloDepth); // crisp: relight normals
    const soloDispMap = bgDispTex(spatial.soloDepth, null); // smoothed: displacement
    disposables.push(soloTex, soloDepthTex, soloDispMap);
    const texel = new THREE.Vector2(2 / 1024, 2 / 1024);
    // FIX 1 OVERSCAN: the mesh is grown by SOLO_OVERSCAN world per side; the
    // cover maps to the inner ow×oh and the outer band (hidden under the mat)
    // carries the whole border taper. uOvs = overscan fraction of the ENLARGED
    // mesh per side, so the shader's cover-uv remap + per-axis taper line up.
    const meshW = ow + 2 * SOLO_OVERSCAN;
    const meshH = oh + 2 * SOLO_OVERSCAN;
    const uOvsX = SOLO_OVERSCAN / meshW;
    const uOvsY = SOLO_OVERSCAN / meshH;
    const soloUniforms = {
      uMap: { value: soloTex },
      uDepth: { value: soloDepthTex },
      uDispMap: { value: soloDispMap },
      uDisp: { value: 0 },
      uCamDist: { value: CAM },
      uZScale: { value: FILL },
      uEdge0: { value: TAP_EDGE0 },
      uEdge1: { value: TAP_EDGE1 },
      uKnee: { value: SOLO_KNEE },
      uKneeComp: { value: SOLO_KNEE_COMP },
      uSceneTilt: { value: SOLO_SCENE_TILT },
      uOvsX: { value: uOvsX },
      uOvsY: { value: uOvsY },
      uTexel: { value: texel },
      uLight: sharedLight,
      uRelief: { value: RELIGHT_NORMAL_GAIN },
    };
    const soloMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(meshW, meshH, 96, 96),
      new THREE.ShaderMaterial({
        uniforms: soloUniforms,
        vertexShader: SP_BG_VERT,
        fragmentShader: SP_SOLO_FRAG,
        toneMapped: false,
      }),
    );
    // SET BACK behind the opening (like the ldi bg plate) so the oversized mesh
    // boundary + taper are occluded by the mat + frame rather than poking past a
    // coplanar frame.
    soloMesh.position.set(0, 0, -SEP_SOLO);
    soloMesh.renderOrder = 0;
    group.add(soloMesh);
    // REGISTRATION (the same math as the ldi bg plate): scale the set-back mesh by
    // k = distCam(solo)/distCam(opening) so its geometry projects pixel-onto the
    // opening at rest. The cover region (inner meshW×meshH minus the overscan
    // band) then lands exactly on the opening; the overscan margin lands beyond
    // it, behind the mat. Measure the true world camera distance first (the FILL
    // group transform rescales child z — never-hardcode rule as ldi).
    scene.updateMatrixWorld(true);
    const soloW = soloMesh.getWorldPosition(new THREE.Vector3());
    const openW = group.getWorldPosition(new THREE.Vector3());
    const kSolo = bgRegistrationScale(cam.position.z, openW.z, soloW.z);
    soloMesh.scale.set(kSolo, kSolo, 1);
    const camDistSolo = cam.position.z - soloW.z;
    soloUniforms.uCamDist.value = camDistSolo;
    // FIX 2 CONTENT-ADAPTIVE relief: a flat graphic poster gets minimal
    // displacement + tilt (letters/lines stay straight, move as a coherent
    // near-flat plane); a photoreal scene keeps full depth. Pipeline value
    // (meta.soloReliefScale) wins; else compute from the cover at load.
    const reliefScale = spatial.reliefScale ?? graphicReliefScale(spatial.solo);
    // Displacement is CONSTANT (not pose-gained): the ray-compensation makes it
    // project to the cover at rest, and constant depth = proportional,
    // non-bouncing motion (see the ldi note above). Solo is one continuous
    // surface so the whole scene already moves as one depth field.
    const soloReliefOn = spatial.relief;
    soloUniforms.uDisp.value = soloReliefOn ? D_SOLO * reliefScale : 0;
    soloUniforms.uSceneTilt.value = SOLO_SCENE_TILT * reliefScale;
    // Verification/tuning hook: expose the solo uniforms so a Playwright sweep can
    // live-adjust uDisp/uKnee/uKneeComp/uSceneTilt without a rebuild per candidate.
    (window as unknown as { __soloUniforms?: unknown }).__soloUniforms = soloUniforms;
    Object.assign(diag, {
      camDistSolo,
      kSolo,
      sepSolo: SEP_SOLO,
      relief: spatial.relief,
      soloReliefOn,
      soloReliefScale: reliefScale,
      soloReliefFromMeta: spatial.reliefScale !== undefined,
      uOvsX,
      uOvsY,
    });
  } else {
    // ── FLAT: the cover as a premium framed print (raw passthrough) ────────
    const aTex = contentTex(art, renderer, false);
    disposables.push(aTex);
    const flatMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(ow, oh, 1, 1),
      new THREE.ShaderMaterial({ uniforms: { uMap: { value: aTex } }, vertexShader: FLAT_VERT, fragmentShader: FLAT_FRAG, toneMapped: false }),
    );
    flatMesh.position.set(0, 0, 0);
    flatMesh.renderOrder = 0;
    group.add(flatMesh);
  }

  // ── OPAQUE MAT RING ── a THIN occluder that hides ENTIRELY under the gold
  // molding (user 2026-07-03: the old 3.5%-lip / full-frame-outer ring showed a
  // black band inside the frame AND black past the ornate gold edge). It only
  // needs to (a) cover the opening edge + the max bg-overscan excursion so the
  // plate edge never peeks into the opening, and (b) reach outward just into the
  // SOLID gold — NOT to the ornate outer edge, where its rectangle poked past
  // the carving. Inner lip = 1.8% (≈0.025 world, > the ~0.016 world orbit
  // excursion, < the gold's FRAME_OVL=0.05 inner overlap so gold covers it);
  // outer = opening + MAT_OUT (0.15 world, mid-molding = under solid gold, well
  // short of FRAME_OUTER=0.29 so the ornate hollows show dark PAGE, not mat).
  // FIXED-WORLD lip (2026-07-03): a width-scaled 0.018·ow exceeded the gold's
  // fixed 0.05 overlap on the SHORT edges of a LANDSCAPE image → black band past
  // the gold there. 0.03 world is > the ~0.016 orbit excursion and < 0.05 on all
  // four sides at ANY aspect, so the gold always covers it.
  // SOLO SPILL FIX (2026-07-05): the set-back solo mesh + full-edge displacement
  // projects the cover right up to the opening edge (and a few px past it under
  // relief). The gold's INNER edge is ornate — hollows in its FRAME_OVL overlap
  // zone [ow/2-FRAME_OVL, ow/2] let the cover show THROUGH the gold onto the
  // molding (the "bright sliver over the frame" the user caught). The old
  // lip=0.03 mat stopped 0.02 short of the gold inner edge, leaving that
  // ornamental ring UN-BACKED. Raise the lip to EXACTLY FRAME_OVL so the opaque
  // mat backs the entire gold-overlap zone: every gold hollow now shows dark mat
  // (reads as a carved recess), never cover → no spill. The mat inner edge lands
  // exactly on the gold inner edge, so the cover meets the gold with no dark gap
  // (cover fills inward of it; the mat/gold cover outward). The cover still fills
  // to the full opening (+overscan) so orbit parallax never retreats it past the
  // mat → no gap under motion either.
  {
    const lip = 0.015; // lands the mat inner edge on the gold bead: backs the gold hollows (no spill),
    // covers no visible art.
    const MAT_OUT = 0.15;
    const inW = ow / 2 - lip;
    const inH = oh / 2 - lip;
    const outW = ow / 2 + MAT_OUT;
    const outH = oh / 2 + MAT_OUT;
    // depthTest:false makes the mat a HARD 2D occluder (SOLO SPILL FIX, part 2,
    // 2026-07-05): at a NEAR subject the solo displacement d can exceed the mesh
    // set-back SEP_SOLO, pushing that patch of cover to a world-z IN FRONT of the
    // mat/gold planes (z≈0.04/0.06). With depthTest on, the depth buffer then
    // REJECTS the opaque mat there and the near cover pokes through the frame
    // (the residual bottom-corner spill). Drawn after the cover (renderOrder 10 >
    // 0), depthTest:false overwrites those cover pixels unconditionally; depthWrite
    // stays on so the gold (0.06, renderOrder 30) still tests/draws over the mat.
    const matMat = new THREE.MeshBasicMaterial({ color: 0x0a0810, depthTest: false });
    const addMat = (w: number, h: number, x: number, y: number): void => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), matMat);
      m.position.set(x, y, 0.04);
      m.renderOrder = 10;
      group.add(m);
    };
    addMat(2 * outW, outH - inH, 0, (inH + outH) / 2); // top
    addMat(2 * outW, outH - inH, 0, -(inH + outH) / 2); // bottom
    addMat(outW - inW, 2 * inH, -(inW + outW) / 2, 0); // left
    addMat(outW - inW, 2 * inH, (inW + outW) / 2, 0); // right
  }

  // ── 9-SLICE ornate gold frame ── crisp corner cartouches + edge strips tiled
  // along their length. Metallic + normal map + env reflection: the shimmer
  // rides the key light, which tracks the pointer. (Keeps ACES + sRGB.)
  const fTex = new THREE.Texture(frm);
  fTex.colorSpace = THREE.SRGBColorSpace;
  fTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  fTex.needsUpdate = true;
  const fNorm = frameNormalTex(frm);
  disposables.push(fTex, fNorm);
  const fMat = new THREE.MeshStandardMaterial({
    map: fTex,
    transparent: true,
    alphaTest: 0.5,
    normalMap: fNorm,
    normalScale: new THREE.Vector2(0.8, 0.8),
    metalness: 1,
    roughness: 0.3,
    envMapIntensity: 1.35,
  });
  const t = FRAME_T;
  const mt = FRAME_MT;
  // Place pieces so the GOLD inner edge meets the image (overlap FRAME_OVL) — no gap.
  const exL = ow / 2 - FRAME_OVL + mt * (0.5 - FRAME_TRANS);
  const eyT = oh / 2 - FRAME_OVL + mt * (0.5 - FRAME_TRANS);
  const iw = 2 * exL - mt; // edge lengths (span between corners)
  const ih = 2 * eyT - mt;
  const framePiece = (
    w: number,
    h: number,
    cx: number,
    cy: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
  ): void => {
    const g = new THREE.PlaneGeometry(w, h, 1, 1);
    const uv = g.attributes.uv as THREE.BufferAttribute;
    uv.setXY(0, u0, v1);
    uv.setXY(1, u1, v1);
    uv.setXY(2, u0, v0);
    uv.setXY(3, u1, v0);
    uv.needsUpdate = true;
    const m = new THREE.Mesh(g, fMat);
    m.position.set(cx, cy, 0.06);
    m.renderOrder = 30; // above content + mat
    group.add(m);
  };
  framePiece(mt, mt, -exL, +eyT, 0, 1 - t, t, 1); // corners
  framePiece(mt, mt, +exL, +eyT, 1 - t, 1 - t, 1, 1);
  framePiece(mt, mt, -exL, -eyT, 0, 0, t, t);
  framePiece(mt, mt, +exL, -eyT, 1 - t, 0, 1, t);
  // Edges: TILE the ornament along its length at natural density (mirrored, odd
  // repeats) so long sides never smear — one frame wraps every aspect cleanly.
  const NATURAL = (mt * (1 - 2 * t)) / t; // world length of one natural strip repeat
  const oddRep = (len: number): number => {
    const x = len / NATURAL;
    let r = Math.max(1, Math.round(x));
    if (r % 2 === 0) r = (x > r ? r + 1 : r - 1) || 1;
    return Math.max(1, r);
  };
  // sa0/sa1 = along-length slice bounds; sb0/sb1 = across-thickness bounds.
  const frameEdge = (
    horizontal: boolean,
    length: number,
    cx: number,
    cy: number,
    sa0: number,
    sa1: number,
    sb0: number,
    sb1: number,
    rep: number,
  ): void => {
    const g = horizontal
      ? new THREE.PlaneGeometry(length, mt, rep, 1)
      : new THREE.PlaneGeometry(mt, length, 1, rep);
    const uv = g.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i += 1) {
      const gu = uv.getX(i);
      const gv = uv.getY(i);
      const f = horizontal ? gu : gv; // fraction along length
      const c = horizontal ? gv : gu; // fraction across thickness
      const b = Math.round(f * rep); // tile boundary index 0..rep
      const aa = b % 2 === 0 ? sa0 : sa1; // mirrored tiling
      const bb = sb0 + c * (sb1 - sb0);
      if (horizontal) uv.setXY(i, aa, bb);
      else uv.setXY(i, bb, aa);
    }
    uv.needsUpdate = true;
    const m = new THREE.Mesh(g, fMat);
    m.position.set(cx, cy, 0.06);
    m.renderOrder = 30;
    group.add(m);
  };
  const repH = oddRep(iw);
  const repV = oddRep(ih);
  frameEdge(true, iw, 0, +eyT, t, 1 - t, 1 - t, 1, repH); // top
  frameEdge(true, iw, 0, -eyT, t, 1 - t, 0, t, repH); // bottom
  frameEdge(false, ih, -exL, 0, t, 1 - t, 0, t, repV); // left
  frameEdge(false, ih, +exL, 0, t, 1 - t, 1 - t, 1, repV); // right

  scene.add(new THREE.AmbientLight(0xfff0d8, 0.34));
  const key = new THREE.DirectionalLight(0xffe6bd, 2.3);
  key.position.set(-1.6, 1.8, 2.4);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x9fb4ff, 0.85);
  rim.position.set(2.0, -0.7, 1.2);
  scene.add(rim);

  // Expose diagnostics for the verification harness (Playwright).
  holder.dataset.sculptMode = mode;
  if (typeof window !== 'undefined') {
    (window as unknown as { __sculptDiag?: unknown }).__sculptDiag = diag;
  }

  /* ── ONE pose function drives live, ambient AND export paths ─────────────
     Drive signals (sx, sy) are normalized to [-1, 1]. Spatial AND solo: camera
     orbit at the same world amps, lookAt origin — planes NEVER move. Flat:
     rigid whole-card tilt. Key light + relight light ride the same drive. At
     rest (0, 0) the pose is EXACTLY neutral: rest frame == source (CI gate). */
  const applyPose = (sx: number, sy: number): void => {
    group.scale.setScalar(FILL);
    if (mode !== 'flat') {
      cam.position.set(ORBIT_AMP_X * sx, ORBIT_AMP_Y * sy, CAM);
      cam.lookAt(0, 0, 0);
    } else {
      pivot.rotation.set(-sy * TILT_AMP_Y, sx * TILT_AMP_X, 0);
    }
    // Rest-fidelity gain (C1): displacement + poster shadow fade in with pose
    // magnitude — at the rest pose every gained uniform is EXACTLY 0, so the
    // rest frame is mathematically the cover render. Same math on live,
    // ambient and export paths (all pose through here).
    const g = poseGain(sx, sy);
    for (const pg of poseGained) pg.u.value = pg.base * g;
    // Verification hook: `window.__sculptNeutralLight = true` pins key light +
    // content relight at their neutral rest values while the CAMERA still
    // orbits (spec: relight is off in Phase-1 CI comparisons), so the ghost /
    // void gates can diff orbit frames against rest without the intended
    // relight field registering as a ghost. Live behavior is unchanged.
    // Verification hook: `window.__sculptLightPose = { lx, ly }` pins the LIGHT
    // drive independent of the camera pose (e.g. rest camera + full-swing
    // light) so the rest_identity relight-variant pass can measure the
    // production relight field in isolation. Live behavior is unchanged.
    const neutralLight =
      (window as unknown as { __sculptNeutralLight?: boolean }).__sculptNeutralLight === true;
    const lightPin = (window as unknown as { __sculptLightPose?: { lx: number; ly: number } })
      .__sculptLightPose;
    let lx = neutralLight ? 0 : sx;
    let ly = neutralLight ? 0 : sy;
    if (!neutralLight && lightPin && Number.isFinite(lightPin.lx) && Number.isFinite(lightPin.ly)) {
      lx = lightPin.lx;
      ly = lightPin.ly;
    }
    key.position.set(-1.6 + lx * KEY_SWING, 1.8 - ly * KEY_SWING, 2.4);
    uLightDir.set(-lx * LIGHT_SWING, ly * LIGHT_SWING, 1.0);
  };

  // ---- interaction: gyroscope (with iOS permission) + pointer/touch ----
  let tx = 0;
  let ty = 0;
  let rxv = 0;
  let ryv = 0;
  let active = false;
  let idle = 0;
  let gyro: { x: number; y: number } | null = null;
  const setT = (px: number, py: number): void => {
    tx = Math.max(-1, Math.min(1, px));
    ty = Math.max(-1, Math.min(1, py));
    active = true;
  };
  const ev = (cx: number, cy: number): void => {
    const r = holder.getBoundingClientRect();
    setT(((cx - r.left) / r.width) * 2 - 1, ((cy - r.top) / r.height) * 2 - 1);
  };
  const onPointer = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') ev(e.clientX, e.clientY);
  };
  const onLeave = (): void => {
    active = false;
  };
  const onTouch = (e: TouchEvent): void => {
    const tt = e.touches[0];
    if (tt) ev(tt.clientX, tt.clientY);
  };
  // Adaptive calibration — the neutral pose continuously drifts toward wherever
  // the phone is held (EMA ≈ 1s to 50%), so the effect never saturates dead.
  let beta0: number | null = null;
  let gamma0: number | null = null;
  const RANGE = 24; // degrees of tilt for full deflection
  const NEUTRAL_EMA = 0.015;
  const onOrient = (e: DeviceOrientationEvent): void => {
    if (e.gamma == null || e.beta == null) return;
    if (beta0 === null) {
      beta0 = e.beta;
      gamma0 = e.gamma;
    } else if (gamma0 !== null) {
      beta0 += NEUTRAL_EMA * (e.beta - beta0);
      gamma0 += NEUTRAL_EMA * (e.gamma - gamma0);
    }
    gyro = {
      x: Math.max(-1, Math.min(1, (e.gamma - (gamma0 ?? 0)) / RANGE)),
      y: Math.max(-1, Math.min(1, (e.beta - beta0) / RANGE)),
    };
  };
  let gyroOn = false;
  // Called from an explicit user gesture (the "enable motion" tap on iOS, or
  // immediately on Android/desktop where no permission gate exists).
  const enableMotion = (): void => {
    if (gyroOn) return;
    beta0 = null; // recalibrate neutral to the current pose
    gamma0 = null;
    const DOE = window.DeviceOrientationEvent as
      | (typeof DeviceOrientationEvent & { requestPermission?: () => Promise<string> })
      | undefined;
    if (!DOE) return;
    if (typeof DOE.requestPermission === 'function') {
      DOE.requestPermission()
        .then((s) => {
          if (s === 'granted') {
            window.addEventListener('deviceorientation', onOrient);
            gyroOn = true;
          }
        })
        .catch(() => undefined);
    } else {
      window.addEventListener('deviceorientation', onOrient);
      gyroOn = true;
    }
  };
  // No-permission-gate platforms: start sensing right away.
  {
    const DOE = window.DeviceOrientationEvent as
      | (typeof DeviceOrientationEvent & { requestPermission?: () => Promise<string> })
      | undefined;
    if (DOE && typeof DOE.requestPermission !== 'function') enableMotion();
  }

  holder.addEventListener('pointermove', onPointer);
  holder.addEventListener('pointerleave', onLeave);
  holder.addEventListener('touchmove', onTouch, { passive: true });
  holder.addEventListener('touchend', onLeave);

  const onResize = (): void => {
    const r = holder.getBoundingClientRect();
    if (!r.width || !r.height) return;
    cam.aspect = r.width / r.height;
    cam.updateProjectionMatrix();
    renderer.setSize(r.width, r.height);
  };
  window.addEventListener('resize', onResize);
  // Re-fit after the lightbox entry animation settles + on any container resize.
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
  ro?.observe(holder);

  const clock = new THREE.Clock();
  let raf = 0;
  let ambElapsed = 0;
  // While a clip is being captured we drive the scene ourselves (offscreen, at
  // export resolution) — the live loop parks so it can't fight the orbit.
  let capturing = false;
  // Offscreen sculpts park here: the rAF keeps ticking (cheap) but skips the
  // render + tilt math, so the context stays warm and resumes instantly.
  let paused = false;
  // The bold download-matching auto-orbit. Initialised from the prop, but
  // toggleable at runtime (the lightbox's "auto play") without a rebuild.
  let ambientFlag = ambient;
  const loop = (): void => {
    raf = requestAnimationFrame(loop);
    if (capturing || paused) {
      clock.getDelta(); // drain dt so we don't jump on resume
      return;
    }
    const dt = Math.min(0.05, clock.getDelta());

    // Verification hook: `window.__sculptStill = true` pins the EXACT rest pose
    // (neutral camera + neutral light) so the CI rest-identity gate can compare
    // the framebuffer byte-for-byte against the source cover deterministically.
    if ((window as unknown as { __sculptStill?: boolean }).__sculptStill === true) {
      applyPose(0, 0);
      renderer.render(scene, cam);
      return;
    }

    // Verification hook: `window.__sculptPose = { sx, sy }` pins an EXACT orbit
    // pose (normalized drive, clamped to [-1, 1]) so the ghost/void numeric
    // gates can capture deterministic max-offset frames. Same applyPose math as
    // live/ambient/export — nothing else moves.
    const pinned = (window as unknown as { __sculptPose?: { sx: number; sy: number } }).__sculptPose;
    if (pinned && Number.isFinite(pinned.sx) && Number.isFinite(pinned.sy)) {
      applyPose(Math.max(-1, Math.min(1, pinned.sx)), Math.max(-1, Math.min(1, pinned.sy)));
      renderer.render(scene, cam);
      return;
    }

    if (ambientFlag) {
      // Ambient auto-orbit: X cos / Y sin at the full drive amplitude (damped by
      // ORBIT_SCALE on long aspects). FRAME 0 IS THE MAX OFFSET — the worst-case
      // frame the mat lip is sized for and the frame every capture starts on.
      // 4.6s period; position + velocity continuous at the loop boundary, so the
      // exported clip loops seamlessly. Identical math runs in captureClip.
      ambElapsed += dt;
      // Period tuned by user review (2026-07-02): 4.6s wobbled, 8.5s dragged;
      // 6.2s is the calm-but-alive middle.
      const ph = (ambElapsed / 6.2) * Math.PI * 2;
      applyPose(ORBIT_SCALE * Math.cos(ph), ORBIT_SCALE * Math.sin(ph));
      renderer.render(scene, cam);
      return;
    }

    let gx = tx;
    let gy = ty;
    if (gyro) {
      gx = gyro.x;
      gy = gyro.y;
      active = true;
    }
    if (!active) {
      idle += dt;
      gx = Math.sin(idle * 0.45) * 0.32;
      gy = Math.cos(idle * 0.38) * 0.22;
    }
    // Damped normalized drive; sy = −gy so pointer-above-centre looks from above.
    ryv += (gx - ryv) * 0.12;
    rxv += (-gy - rxv) * 0.12;
    applyPose(ryv, rxv);
    renderer.render(scene, cam);
  };
  raf = requestAnimationFrame(loop);

  // Render a deterministic orbit OFFSCREEN at export resolution and hand each
  // finished frame (sRGB RGBA, top-down) back to the caller to encode. Uses the
  // exact live scene/materials/lighting + the exact live pose math (applyPose),
  // so the clip matches the on-screen motion for BOTH spatial and flat gens.
  const captureClip: CaptureClip = async (opts) => {
    const { width, height, frames, ampX = 1, ampY = 1, watermark = false, onFrame } = opts;
    capturing = true;
    const prevAspect = cam.aspect;
    const prevBg = scene.background;
    const prevPR = renderer.getPixelRatio();
    const prevSize = renderer.getSize(new THREE.Vector2());
    // OPAQUE radial-vignette backdrop, rendered as part of the scene so the
    // export frames are fully opaque — premium gallery-wall feel.
    const bgC = document.createElement('canvas');
    bgC.width = 64;
    bgC.height = 64;
    const bx = bgC.getContext('2d');
    if (bx) {
      const g = bx.createRadialGradient(32, 26, 2, 32, 32, 46);
      g.addColorStop(0, '#181520');
      g.addColorStop(1, '#070509');
      bx.fillStyle = g;
      bx.fillRect(0, 0, 64, 64);
    }
    const bgTexC = new THREE.CanvasTexture(bgC);
    bgTexC.colorSpace = THREE.SRGBColorSpace;
    // 3D brand mark — a gold-foil "Gilding.app" plane parented into the content
    // group so it parallaxes with the orbit (free tier only).
    const wm = watermark ? buildWatermark(renderer, ow, oh) : null;
    if (wm) group.add(wm.mesh);
    // Read frames from the SCREEN render (drawing buffer) — colour-faithful. A
    // WebGLRenderTarget read-back sRGB-encodes a second time (milky exports).
    const readCv = document.createElement('canvas');
    readCv.width = width;
    readCv.height = height;
    const readCtx = readCv.getContext('2d', { willReadFrequently: true });
    if (readCtx) {
      readCtx.imageSmoothingEnabled = true;
      readCtx.imageSmoothingQuality = 'high';
    }
    // SUPERSAMPLE the export: render at SS× then downscale on read-back. The
    // whale's SHARP silhouette edge aliases hard at native res — the jaggies CRAWL
    // as it orbits, which reads as the edge "buckling/waving" in downloaded clips.
    // 2× gives the export the same smooth edge the retina live preview already has
    // (verified: PR1 edge is stair-stepped, PR2 is clean). BUT a 2× HD buffer over
    // ~186 frames exhausts memory on phones — iOS Safari kills + reloads the tab
    // mid-render (white flash ~80-90%). So supersample ONLY where there's headroom:
    // desktop-class devices with real RAM, never on coarse-pointer / low-memory /
    // limited-cores devices, and never past the safe GL buffer size.
    const nav = navigator as Navigator & { deviceMemory?: number; hardwareConcurrency?: number };
    const coarsePointer =
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const lowMemory = typeof nav.deviceMemory === 'number' && nav.deviceMemory < 8;
    const fewCores = typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 4;
    const canSupersample =
      !coarsePointer && !lowMemory && !fewCores && Math.max(width, height) * 2 <= 4096;
    const SS = canSupersample ? 2 : 1;
    try {
      renderer.setPixelRatio(1);
      renderer.setSize(width * SS, height * SS, false); // SS× buffer, keep CSS size
      cam.aspect = width / height;
      cam.updateProjectionMatrix();
      scene.background = bgTexC;
      renderer.setRenderTarget(null);
      for (let i = 0; i < frames; i += 1) {
        const ph = (i / frames) * Math.PI * 2;
        // Same pose math as the live ambient orbit (frame 0 = max offset).
        applyPose(ampX * ORBIT_SCALE * Math.cos(ph), ampY * ORBIT_SCALE * Math.sin(ph));
        renderer.render(scene, cam);
        if (readCtx) {
          readCtx.drawImage(renderer.domElement, 0, 0, width, height); // top-down, no flip
          await onFrame(readCtx.getImageData(0, 0, width, height).data, width, height, i);
        }
      }
    } finally {
      if (wm) {
        group.remove(wm.mesh);
        wm.dispose();
      }
      bgTexC.dispose();
      scene.background = prevBg;
      applyPose(0, 0);
      pivot.rotation.set(0, 0, 0);
      cam.position.set(0, 0, CAM);
      cam.lookAt(0, 0, 0);
      cam.aspect = prevAspect;
      cam.updateProjectionMatrix();
      renderer.setPixelRatio(prevPR);
      renderer.setSize(prevSize.x, prevSize.y, false);
      capturing = false;
      onResize(); // re-fit to the holder
    }
  };

  const dispose = (): void => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('deviceorientation', onOrient);
    ro?.disconnect();
    holder.removeEventListener('pointermove', onPointer);
    holder.removeEventListener('pointerleave', onLeave);
    holder.removeEventListener('touchmove', onTouch);
    holder.removeEventListener('touchend', onLeave);
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else if (mat) mat.dispose();
    });
    envTex.dispose();
    for (const d of disposables) d.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
  };

  const setPaused = (p: boolean): void => {
    paused = p;
  };
  const setAmbient = (on: boolean): void => {
    if (on && !ambientFlag) ambElapsed = 0; // start the orbit from a clean phase
    ambientFlag = on;
  };

  return { dispose, enableMotion, captureClip, setPaused, setAmbient };
}

export function LivingSculpt({
  src,
  spatialUrl,
  aspect = 2 / 3,
  className,
  alt = '',
  heightCap = '82svh',
  paused = false,
  motionHint = true,
  ambient = false,
  onCaptureReady,
  onControlsReady,
  onSpatialReady,
}: {
  src: string;
  /** Base URL of this gilding's spatial2 asset set, e.g.
   *  `https://api.gilding.app/images/spatial2/<genId>-<coverIdx>`. ONE fetch of
   *  `<spatialUrl>/meta.json` decides the render: `mode:"ldi", v:2` → the
   *  2-layer spatial scene; `mode:"solo", v:2` → the continuous-mesh solo
   *  scene; ANYTHING else (absent prop, 404, load error) → the premium flat
   *  framed print. */
  spatialUrl?: string;
  /** @deprecated legacy asset prop — ignored; spatial2 gating replaced it. */
  depthUrl?: string;
  /** @deprecated legacy asset prop — ignored; spatial2 gating replaced it. */
  bgUrl?: string;
  /** The gilding's aspect (width / height). Picks the matching frame and sets the
   *  canvas shape so the whole image fits with no crop or gap. */
  aspect?: number;
  className?: string;
  alt?: string;
  /** CSS length capping the frame's HEIGHT — the piece is the largest that fits
   *  `min(92vw wide, heightCap tall)`. Callers with controls beneath (the gallery
   *  lightbox) pass a smaller cap so the piece + controls fit one screen on mobile
   *  instead of pushing the buttons off the bottom. */
  heightCap?: string;
  /** Park the render loop while offscreen (keeps the context warm). Lets several
   *  sculpts share a page with only the visible one drawing — the 60fps guard. */
  paused?: boolean;
  /** Show the iOS "Tap to tilt with your phone" affordance. Off for ambient/
   *  display uses (e.g. the landing demos) that should just auto-orbit. */
  motionHint?: boolean;
  /** Drive a bold, legible circular auto-orbit (like the exported clip) instead
   *  of the lightbox's subtle idle drift — for ambient demo placements. */
  ambient?: boolean;
  /** Receives the orbit-capture function once the live scene is ready (and null
   *  on teardown / when WebGL is unavailable) so a caller can export a motion
   *  clip. Works for BOTH spatial and flat gens (flat exports the card tilt). */
  onCaptureReady?: (capture: CaptureClip | null) => void;
  /** Fires once the scene is built. `true` → a validated spatial2 asset set
   *  loaded (2-layer ldi parallax OR continuous-mesh solo). `false` → flat
   *  presentation (still premium, still exportable — the whole-card tilt). */
  onSpatialReady?: (ready: boolean) => void;
  /** Receives imperative controls (enable phone-tilt, toggle auto-play) once the
   *  live scene is ready, so a host can render those affordances outside the
   *  canvas — and `null` on teardown / when WebGL is unavailable. Also signals
   *  whether the device can be tilted (an explicit-permission gyro, i.e. iOS). */
  onControlsReady?: (controls: (SculptControls & { canTilt: boolean }) | null) => void;
}) {
  const reduced = useReducedMotion();
  const apiRef = useRef<SculptApi | null>(null);
  const pausedRef = useRef(paused);
  // The frame's OUTER aspect (the canvas shape) for this gilding — the 9-slice
  // molding wraps the image, so the canvas matches and the image fits exactly.
  const frameAspect = frameOuterAspect(aspect);
  const holderRef = useRef<HTMLDivElement>(null);
  const enableRef = useRef<(() => void) | null>(null);
  const [fallback, setFallback] = useState(false);
  const [motionOn, setMotionOn] = useState(false);
  // iOS (13+) requires an explicit user gesture to grant motion access.
  const needsMotionTap =
    typeof window !== 'undefined' &&
    typeof window.DeviceOrientationEvent !== 'undefined' &&
    typeof (window.DeviceOrientationEvent as unknown as { requestPermission?: unknown })
      .requestPermission === 'function';

  useEffect(() => {
    if (reduced) {
      setFallback(true);
      return undefined;
    }
    try {
      const test = document.createElement('canvas');
      if (!test.getContext('webgl') && !test.getContext('experimental-webgl')) {
        setFallback(true);
        return undefined;
      }
    } catch {
      setFallback(true);
      return undefined;
    }
    const holder = holderRef.current;
    if (!holder) return undefined;
    let disposed = false;
    let api: SculptApi | null = null;
    void (async () => {
      try {
        // The cover + frame + (maybe) the spatial2 set, in parallel. loadSpatial2
        // resolves null on ANY miss — the scene builder then renders flat.
        const [art, frm, spatial] = await Promise.all([
          loadImg(src),
          loadImg(FRAME_SRC),
          spatialUrl ? loadSpatial2(spatialUrl) : Promise.resolve(null),
        ]);
        if (disposed) return;
        api = buildScene(holder, art, frm, spatial, ambient);
        api.setPaused(pausedRef.current); // honour current visibility immediately
        apiRef.current = api;
        enableRef.current = api.enableMotion;
        onCaptureReady?.(api.captureClip);
        onControlsReady?.({
          enableMotion: api.enableMotion,
          setAmbient: api.setAmbient,
          canTilt: needsMotionTap,
        });
        onSpatialReady?.(spatial !== null);
      } catch (e) {
        console.warn('LivingSculpt failed; using flat image', e);
        setFallback(true);
      }
    })();
    return () => {
      disposed = true;
      enableRef.current = null;
      apiRef.current = null;
      onCaptureReady?.(null);
      onControlsReady?.(null);
      onSpatialReady?.(false);
      api?.dispose();
    };
  }, [reduced, src, spatialUrl, ambient, needsMotionTap, onCaptureReady, onControlsReady, onSpatialReady]);

  // Push visibility changes to the live scene without rebuilding it.
  useEffect(() => {
    pausedRef.current = paused;
    apiRef.current?.setPaused(paused);
  }, [paused]);

  // Largest size that fits the viewport at the right shape (heightCap tall or 92vw
  // wide, whichever is smaller) — adapts to the aspect and leaves room for any
  // controls the caller stacks beneath it.
  const fitWidth = `min(92vw, calc(${heightCap} * ${frameAspect}))`;

  if (reduced || fallback) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn('block', className)}
        style={{ width: `min(92vw, calc(${heightCap} * ${aspect}))` }}
      />
    );
  }
  return (
    <div
      className={cn('relative select-none', className)}
      style={{ aspectRatio: String(frameAspect), width: fitWidth }}
    >
      <div ref={holderRef} aria-label={alt} className="absolute inset-0 touch-none" />
      {motionHint && needsMotionTap && !motionOn && (
        <button
          type="button"
          onClick={() => {
            enableRef.current?.();
            setMotionOn(true);
          }}
          className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-accent/45 bg-black/45 px-4 py-2 font-display text-caption text-[#f0d79a] backdrop-blur-sm transition-colors hover:bg-black/60"
        >
          Tap to tilt with your phone
        </button>
      )}
    </div>
  );
}
